import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CreditorService } from '../creditor.service';
import { CreditorGroupInfo } from '../../interfaces/creditorGroupInfo.interface';
import { CreditorGroupAuditLogEntry } from '../../interfaces/creditorGroupAuditLogEntry.interface';
import { ErrorModalService } from '../../modal/error.modal.service';
import { YesNoModalService } from '../../modal/yes-no.modal.service';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { CreditorGroupTransferModalComponent, TransferResult } from '../creditor-group-transfer-modal/creditor-group-transfer-modal.component';
import { CreditorClaimProofOfDebtSummary } from '../../interfaces/creditorClaimProofOfDebtSummary.interface';
import { AuditLogPage } from '../../interfaces/auditLogPage.interface';

@Component({
    selector: 'app-creditor-group',
    templateUrl: './creditor-group.component.html',
    styleUrls: ['./creditor-group.component.scss'],
    animations: [fadeInAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorGroupComponent implements OnInit, OnDestroy {
  public group: CreditorGroupInfo | null = null;
  public inviteForm: FormGroup;
  public isLoading = true;
  public isSending = false;
  public inviteSent = false;

  public isOpeningTransferModal = false;

  public auditLog: CreditorGroupAuditLogEntry[] = [];
  public isLoadingAuditLog = false;
  public auditLogAction = '';
  public auditLogSearch = '';
  public auditLogHasMore = false;
  public auditLogNewBatchStart: number | null = null;
  private auditLogSnapshotMaxUtc: string | null = null;
  private readonly auditPageSize = 20;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly auditLogRequest$ = new Subject<{ reset: boolean; action: string; search: string; pageSize: number; beforeUtc?: string; beforeId?: string; snapshotMaxUtc?: string }>();

  constructor(
    private creditorService: CreditorService,
    private errorModalService: ErrorModalService,
    private yesNoModal: YesNoModalService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private ngbModal: NgbModal
  ) {}

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.auditLogRequest$.pipe(
      switchMap(request => {
        if (request.reset) {
          this.auditLog = [];
          this.auditLogSnapshotMaxUtc = null;
        }
        this.isLoadingAuditLog = true;
        return this.creditorService.getGroupAuditLog(
          request.action || undefined,
          request.search || undefined,
          request.pageSize,
          request.beforeUtc,
          request.beforeId,
          request.snapshotMaxUtc
        ).pipe(
          map(page => ({ page, reset: request.reset })),
          catchError(() => of({ page: { entries: [], snapshotMaxUtc: null as string, hasMore: false } as AuditLogPage, reset: request.reset })),
          finalize(() => this.isLoadingAuditLog = false)
        );
      })
    ).subscribe(({ page, reset }) => {
      this.auditLogHasMore = page.hasMore;
      if (reset) {
        this.auditLogSnapshotMaxUtc = page.snapshotMaxUtc;
        this.auditLogNewBatchStart = null;
      } else {
        this.auditLogNewBatchStart = this.auditLog.length;
        if (page.snapshotMaxUtc) this.auditLogSnapshotMaxUtc = page.snapshotMaxUtc;
      }
      this.auditLog = [...this.auditLog, ...page.entries];
    });

    this.load();
    this.loadAuditLog(true);
  }

  ngOnDestroy(): void {
    this.auditLogRequest$.complete();
  }

  private load(): void {
    this.isLoading = true;
    this.creditorService.getMyGroup().subscribe(
      result => {
        this.group = result;
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
        this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('group.error-load'));
      }
    );
  }

  public loadAuditLog(reset: boolean): void {
    const lastEntry = !reset && this.auditLog.length > 0 ? this.auditLog[this.auditLog.length - 1] : null;
    this.auditLogRequest$.next({
      reset,
      action: this.auditLogAction,
      search: this.auditLogSearch,
      pageSize: this.auditPageSize,
      beforeUtc: lastEntry?.createdUtc,
      beforeId: lastEntry?.id,
      snapshotMaxUtc: !reset ? this.auditLogSnapshotMaxUtc ?? undefined : undefined
    });
  }

  public loadMoreAuditLog(): void {
    this.loadAuditLog(false);
  }

  public onAuditActionChange(value: string): void {
    this.auditLogAction = value;
    this.loadAuditLog(true);
  }

  public onAuditSearchChange(value: string): void {
    this.auditLogSearch = value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadAuditLog(true), 350);
  }

  private expandedAuditCases = new Set<string>();

  public getAuditIcon(action: string): string[] {
    const icons: Record<string, string[]> = {
      GroupCreated:    ['fas', 'users'],
      MemberJoined:    ['fas', 'sign-in-alt'],
      MemberRemoved:   ['fas', 'user-minus'],
      MemberLeft:      ['fas', 'sign-out-alt'],
      InviteSent:      ['fas', 'paper-plane'],
      InviteRevoked:   ['fas', 'times-circle'],
      CaseTransferred: ['fas', 'exchange-alt']
    };
    return icons[action] ?? ['fas', 'circle'];
  }

  public getAuditIconClass(action: string): string {
    const classes: Record<string, string> = {
      GroupCreated:    'audit-icon--blue',
      MemberJoined:    'audit-icon--green',
      MemberRemoved:   'audit-icon--red',
      MemberLeft:      'audit-icon--orange',
      InviteSent:      'audit-icon--blue',
      InviteRevoked:   'audit-icon--gray',
      CaseTransferred: 'audit-icon--purple'
    };
    return classes[action] ?? 'audit-icon--gray';
  }

  public isMultiCaseTransfer(entry: CreditorGroupAuditLogEntry): boolean {
    return entry.action === 'CaseTransferred' && (entry.detail?.includes('|') ?? false);
  }

  public getAuditDescription(entry: CreditorGroupAuditLogEntry): string {
    const actor = entry.actorName;
    const target = entry.targetName || this.translate.instant('group.audit-unknown-person');
    if (this.isMultiCaseTransfer(entry)) {
      const count = entry.detail.split('|').length;
      return this.translate.instant('group.action-desc-CaseTransferred-multi', { actor, count, target });
    }
    return this.translate.instant(`group.action-desc-${entry.action}`, { actor, target, detail: entry.detail });
  }

  public isAuditCasesExpanded(entryId: string): boolean {
    return this.expandedAuditCases.has(entryId);
  }

  public toggleAuditCases(entryId: string): void {
    if (this.expandedAuditCases.has(entryId)) this.expandedAuditCases.delete(entryId);
    else this.expandedAuditCases.add(entryId);
  }

  public getCaseNames(entry: CreditorGroupAuditLogEntry): string[] {
    return entry.detail ? entry.detail.split('|').filter(name => name.trim().length > 0) : [];
  }

  public sendInvite(): void {
    if (this.inviteForm.invalid) { this.inviteForm.markAllAsTouched(); return; }
    this.isSending  = true;
    this.inviteSent = false;

    this.creditorService.sendGroupInvitation(this.inviteForm.value.email).subscribe(
      () => {
        this.isSending  = false;
        this.inviteSent = true;
        this.inviteForm.reset();
        this.load();
        this.loadAuditLog(true);
      },
      err => {
        this.isSending = false;
        const message = typeof err?.error === 'string' ? err.error : this.translate.instant('group.error-send-invite');
        this.errorModalService.open(this.translate.instant('error.title'), message);
      }
    );
  }

  public revokeInvitation(invitationId: string): void {
    this.yesNoModal.open(this.translate.instant('group.modal-revoke-title'), this.translate.instant('group.modal-revoke-message'), {
      buttonCallback: () => {
        this.creditorService.revokeGroupInvitation(invitationId).subscribe(
          () => { this.load(); this.loadAuditLog(true); },
          () => this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('group.error-revoke'))
        );
      }
    });
  }

  public get sortedMembers() {
    return (this.group?.members ?? []).slice().sort((memberA, memberB) => (memberB.isCurrentUser ? 1 : 0) - (memberA.isCurrentUser ? 1 : 0));
  }

  public removeMember(userId: string, name: string, isSelf: boolean): void {
    if (!isSelf) {
      this.yesNoModal.open(
        this.translate.instant('group.modal-remove-title'),
        this.translate.instant('group.modal-remove-message', { name }),
        {
          buttonCallback: () => {
            this.creditorService.removeGroupMember(userId).subscribe(
              () => { this.load(); this.loadAuditLog(true); },
              () => this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('group.error-remove-member'))
            );
          }
        }
      );
      return;
    }

    this.isOpeningTransferModal = true;

    this.creditorService.getMyCasesForTransfer().subscribe(
      ownCases => {
        const otherMembers = (this.group?.members ?? []).filter(member => !member.isCurrentUser);

        if (ownCases.length === 0 || otherMembers.length === 0) {
          this.isOpeningTransferModal = false;
          this.openLeaveModal(userId);
          return;
        }

        const modalRef = this.ngbModal.open(CreditorGroupTransferModalComponent, { centered: true, size: 'lg', scrollable: true });
        modalRef.componentInstance.cases = ownCases;
        modalRef.componentInstance.members = otherMembers;
        modalRef.componentInstance.isLoading = true;
        this.isOpeningTransferModal = false;

        modalRef.result.then(
          (result: TransferResult) => {
            if (!result.leave) return;
            if (result.transfers.length === 0) {
              this.executeLeave(userId);
              return;
            }
            this.creditorService.transferCases(result.transfers).subscribe(
              () => this.executeLeave(userId),
              () => this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('group.error-transfer'))
            );
          },
          () => {}
        );

        forkJoin(
          ownCases.map(creditorCase =>
            this.creditorService.getMyClaimsForCase(creditorCase.caseSummary.caseId).pipe(
              catchError(() => of(null))
            )
          )
        ).subscribe(
          claimsResults => {
            const claimsMap: Record<string, CreditorClaimProofOfDebtSummary[]> = {};
            ownCases.forEach((creditorCase, idx) => {
              claimsMap[creditorCase.id] = claimsResults[idx] ?? [];
            });
            try {
              modalRef.componentInstance.claimsMap = claimsMap;
              modalRef.componentInstance.isLoading = false;
            } catch { }
          },
          () => {
            try {
              modalRef.componentInstance.claimsMap = {};
              modalRef.componentInstance.isLoading = false;
            } catch { }
          }
        );
      },
      () => {
        this.isOpeningTransferModal = false;
        this.openLeaveModal(userId);
      }
    );
  }

  private openLeaveModal(userId: string): void {
    this.yesNoModal.open(
      this.translate.instant('group.modal-leave-title'),
      this.translate.instant('group.modal-leave-message'),
      { buttonCallback: () => this.executeLeave(userId) }
    );
  }

  private executeLeave(userId: string): void {
    this.creditorService.removeGroupMember(userId).subscribe(
      () => { this.load(); this.loadAuditLog(true); },
      () => this.errorModalService.open(this.translate.instant('error.title'), this.translate.instant('group.error-leave-group'))
    );
  }

}
